import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import TextOverlay from './TextOverlay';
import { TextLabel } from '../types';

// ============================================================================
// TYPES
// ============================================================================

export interface SelectionBox {
  /** X position relative to natural image width (0-1 normalized) */
  x: number;
  /** Y position relative to natural image height (0-1 normalized) */
  y: number;
  /** Width relative to natural image width (0-1 normalized) */
  width: number;
  /** Height relative to natural image height (0-1 normalized) */
  height: number;
  /** Absolute pixel coordinates for display */
  displayX: number;
  displayY: number;
  displayWidth: number;
  displayHeight: number;
}

export interface ImageCanvasProps {
  /** URL of the image to display */
  imageUrl: string;
  /** Current selection tool */
  tool: 'box' | 'brush' | null;
  /** Callback when selection changes */
  onSelectionChange: (selection: SelectionBox | null) => void;
  /** Current selection (controlled) */
  selection: SelectionBox | null;
  /** Whether the canvas is disabled (during processing) */
  disabled?: boolean;
  /** Callback for undo (Ctrl+Z) */
  onUndo?: () => void;
  /** Callback for redo (Ctrl+Y / Ctrl+Shift+Z) */
  onRedo?: () => void;
  /** Text labels to overlay on the image */
  textLabels?: TextLabel[];
  /** Callback when text labels change */
  onTextLabelsChange?: (labels: TextLabel[]) => void;
  /** Optional className */
  className?: string;
}

// Zoom levels
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

// ============================================================================
// COMPONENT
// ============================================================================

const ImageCanvas: React.FC<ImageCanvasProps> = ({
  imageUrl,
  tool,
  onSelectionChange,
  selection,
  disabled = false,
  onUndo,
  onRedo,
  textLabels = [],
  onTextLabelsChange,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [currentBox, setCurrentBox] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<{ natural: { width: number; height: number }; display: { width: number; height: number } } | null>(null);
  
  // Zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isSpaceDown, setIsSpaceDown] = useState(false);
  
  // Hide hint after first selection
  const [hasInteracted, setHasInteracted] = useState(false);

  // Listen for keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture keyboard shortcuts when typing in input fields
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }
      
      // Spacebar for pan mode
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        setIsSpaceDown(true);
        return;
      }
      
      // Escape to clear selection
      if (e.key === 'Escape' && selection) {
        e.preventDefault();
        onSelectionChange(null);
        return;
      }
      
      // Ctrl+Z / Cmd+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        onUndo?.();
        return;
      }
      
      // Ctrl+Y / Cmd+Y or Ctrl+Shift+Z / Cmd+Shift+Z for redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        onRedo?.();
        return;
      }
    };
    
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setIsSpaceDown(false);
        setIsPanning(false);
      }
    };
    
    // Reset spacebar state when window loses focus
    const handleBlur = () => {
      setIsSpaceDown(false);
      setIsPanning(false);
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, [selection, onSelectionChange, onUndo, onRedo]);

  // Update dimensions when image loads or resizes
  useEffect(() => {
    const updateDimensions = () => {
      if (imageRef.current && imageRef.current.complete) {
        setImageDimensions({
          natural: {
            width: imageRef.current.naturalWidth,
            height: imageRef.current.naturalHeight,
          },
          display: {
            width: imageRef.current.clientWidth,
            height: imageRef.current.clientHeight,
          },
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [imageLoaded, zoom]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(MIN_ZOOM, prev - ZOOM_STEP));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Mouse wheel: Ctrl = zoom, regular scroll = pan when zoomed
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      // Zoom with Ctrl+scroll
      e.preventDefault();
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      setZoom(prev => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev + delta)));
    } else if (zoom > 1) {
      // Pan with regular scroll when zoomed in
      e.preventDefault();
      setPan(prev => ({
        x: prev.x - e.deltaX,
        y: prev.y - e.deltaY,
      }));
    }
  }, [zoom]);

  /**
   * Convert screen coordinates to image coordinates (in display pixels, unscaled)
   * Accounts for zoom transform - the bounding rect includes transforms,
   * so we divide by zoom to get the original image coordinates.
   */
  const screenToImageCoords = useCallback((screenX: number, screenY: number): { x: number; y: number } | null => {
    if (!imageRef.current || !imageDimensions) return null;
    
    const rect = imageRef.current.getBoundingClientRect();
    
    // rect includes CSS transforms, so position relative to rect is already in scaled space
    // Divide by zoom to convert to unscaled image coordinates
    const x = (screenX - rect.left) / zoom;
    const y = (screenY - rect.top) / zoom;
    
    // Clamp to image bounds (in unscaled display pixels)
    const clampedX = Math.max(0, Math.min(imageDimensions.display.width, x));
    const clampedY = Math.max(0, Math.min(imageDimensions.display.height, y));
    
    return { x: clampedX, y: clampedY };
  }, [zoom, imageDimensions]);

  /**
   * Convert image coordinates to normalized (0-1) coordinates
   */
  const imageToNormalized = useCallback((imageX: number, imageY: number): { x: number; y: number } => {
    if (!imageDimensions) return { x: 0, y: 0 };
    return {
      x: Math.max(0, Math.min(1, imageX / imageDimensions.display.width)),
      y: Math.max(0, Math.min(1, imageY / imageDimensions.display.height)),
    };
  }, [imageDimensions]);

  // Handle mouse down - start drawing or panning
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Middle mouse, right click, or spacebar+left click starts panning
    if (e.button === 1 || e.button === 2 || (e.button === 0 && isSpaceDown)) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      return;
    }
    
    if (disabled || tool !== 'box' || !imageDimensions) return;
    
    e.preventDefault();
    setHasInteracted(true); // Hide the hint
    
    const coords = screenToImageCoords(e.clientX, e.clientY);
    if (!coords) return;

    // Only start if within image bounds (coords are already clamped)
    setIsDrawing(true);
    setStartPoint({ x: coords.x, y: coords.y });
    setCurrentBox({ x: coords.x, y: coords.y, width: 0, height: 0 });
    onSelectionChange(null); // Clear previous selection
  }, [disabled, tool, imageDimensions, onSelectionChange, pan, isSpaceDown, screenToImageCoords]);

  // Handle mouse move - update box or pan
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // Handle panning
    if (isPanning) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
      return;
    }
    
    if (!isDrawing || !startPoint || !imageDimensions) return;
    
    const coords = screenToImageCoords(e.clientX, e.clientY);
    if (!coords) return;

    // Calculate box (handle negative dimensions for reverse drag)
    const x = Math.min(startPoint.x, coords.x);
    const y = Math.min(startPoint.y, coords.y);
    const width = Math.abs(coords.x - startPoint.x);
    const height = Math.abs(coords.y - startPoint.y);

    setCurrentBox({ x, y, width, height });
  }, [isDrawing, startPoint, imageDimensions, isPanning, panStart, screenToImageCoords]);

  // Handle mouse up - finalize selection or stop panning
  const handleMouseUp = useCallback(() => {
    if (isPanning) {
      setIsPanning(false);
      return;
    }
    
    if (!isDrawing || !currentBox || !imageDimensions) {
      setIsDrawing(false);
      return;
    }

    // Only create selection if box is meaningful (> 5px to allow small selections)
    if (currentBox.width > 5 && currentBox.height > 5) {
      const normalizedX = currentBox.x / imageDimensions.display.width;
      const normalizedY = currentBox.y / imageDimensions.display.height;
      const normalizedWidth = currentBox.width / imageDimensions.display.width;
      const normalizedHeight = currentBox.height / imageDimensions.display.height;

      onSelectionChange({
        x: normalizedX,
        y: normalizedY,
        width: normalizedWidth,
        height: normalizedHeight,
        displayX: currentBox.x,
        displayY: currentBox.y,
        displayWidth: currentBox.width,
        displayHeight: currentBox.height,
      });
    }

    setIsDrawing(false);
    setStartPoint(null);
    setCurrentBox(null);
  }, [isDrawing, currentBox, imageDimensions, onSelectionChange]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    if (isDrawing) {
      handleMouseUp();
    }
  }, [isDrawing, handleMouseUp]);

  // Clear selection on click outside
  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    if (e.target === containerRef.current && selection) {
      onSelectionChange(null);
    }
  }, [selection, onSelectionChange]);

  // Get display box coordinates (for rendering the selection overlay)
  const getDisplayBox = useCallback(() => {
    if (currentBox) return currentBox;
    if (selection && imageDimensions) {
      return {
        x: selection.x * imageDimensions.display.width,
        y: selection.y * imageDimensions.display.height,
        width: selection.width * imageDimensions.display.width,
        height: selection.height * imageDimensions.display.height,
      };
    }
    return null;
  }, [currentBox, selection, imageDimensions]);

  const displayBox = getDisplayBox();

  return (
    <div 
      ref={containerRef}
      className={`flex-1 bg-slate-950 flex items-center justify-center p-4 md:p-8 overflow-auto relative ${className}`}
      onClick={handleContainerClick}
      onWheel={handleWheel}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Blueprint grid background */}
      <div 
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(#0d9488 1px, transparent 1px), linear-gradient(90deg, #0d9488 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        }}
      />

      {/* Zoom controls */}
      <div className="absolute top-4 left-4 z-20 flex flex-col gap-1 bg-slate-800/90 rounded-lg p-1 backdrop-blur-sm border border-slate-700">
        <button
          onClick={handleZoomIn}
          className="p-2 hover:bg-slate-700 rounded-md transition-colors text-slate-300 hover:text-white"
          title="Zoom in (Ctrl+scroll)"
        >
          <ZoomIn size={18} />
        </button>
        <button
          onClick={handleZoomOut}
          className="p-2 hover:bg-slate-700 rounded-md transition-colors text-slate-300 hover:text-white"
          title="Zoom out (Ctrl+scroll)"
        >
          <ZoomOut size={18} />
        </button>
        <button
          onClick={handleZoomReset}
          className="p-2 hover:bg-slate-700 rounded-md transition-colors text-slate-300 hover:text-white"
          title="Reset zoom"
        >
          <Maximize2 size={18} />
        </button>
        <div className="text-xs text-center text-slate-400 py-1 border-t border-slate-700 mt-1">
          {Math.round(zoom * 100)}%
        </div>
      </div>

      {/* Image container with zoom/pan transform */}
      <div 
        className="relative"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: 'center center',
          cursor: isPanning ? 'grabbing' : (isSpaceDown ? 'grab' : undefined),
        }}
      >
        <img
          ref={imageRef}
          src={imageUrl}
          alt="Floor plan"
          className={`max-w-full max-h-[calc(100vh-200px)] object-contain rounded-lg shadow-2xl ${
            tool === 'box' && !disabled ? 'cursor-crosshair' : ''
          }`}
          onLoad={() => setImageLoaded(true)}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          draggable={false}
        />

        {/* Selection overlay */}
        {displayBox && displayBox.width > 0 && displayBox.height > 0 && (
          <div
            className="absolute pointer-events-none"
            style={{
              left: displayBox.x,
              top: displayBox.y,
              width: displayBox.width,
              height: displayBox.height,
              border: '2px dashed #14b8a6',
              backgroundColor: 'rgba(20, 184, 166, 0.15)',
              boxShadow: '0 0 0 2000px rgba(0, 0, 0, 0.3)',
            }}
          >
            {/* Corner handles */}
            <div className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-teal-500 rounded-full border-2 border-white shadow" />
            <div className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-teal-500 rounded-full border-2 border-white shadow" />
            <div className="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-teal-500 rounded-full border-2 border-white shadow" />
            <div className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-teal-500 rounded-full border-2 border-white shadow" />
            
            {/* Dimension label */}
            {!isDrawing && selection && (
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-teal-600 text-white text-xs font-medium px-2 py-1 rounded shadow whitespace-nowrap">
                {Math.round(selection.width * (imageDimensions?.natural.width || 0))} × {Math.round(selection.height * (imageDimensions?.natural.height || 0))} px
              </div>
            )}
          </div>
        )}

        {/* Text labels overlay */}
        {textLabels.length > 0 && imageDimensions && onTextLabelsChange && (
          <TextOverlay
            labels={textLabels}
            onLabelsChange={onTextLabelsChange}
            containerWidth={imageDimensions.display.width}
            containerHeight={imageDimensions.display.height}
            disabled={disabled}
            zoom={zoom}
          />
        )}

        {/* Tool hint overlay - only shows before first interaction */}
        {tool === 'box' && !selection && !isDrawing && !disabled && !hasInteracted && (
          <div 
            className="absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-none opacity-80"
          >
            <div className="bg-slate-800/90 text-slate-300 text-xs px-3 py-1.5 rounded-full backdrop-blur-sm border border-slate-600">
              Click and drag to select
            </div>
          </div>
        )}

        {/* Brush tool placeholder */}
        {tool === 'brush' && !disabled && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-amber-600/80 text-white text-sm px-4 py-2 rounded-lg backdrop-blur-sm border border-amber-400/30">
              Brush tool coming soon - use Box Select for now
            </div>
          </div>
        )}

        {/* Processing overlay */}
        {disabled && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm rounded-lg">
            <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mb-4" />
            <div className="text-white text-lg font-medium">Processing...</div>
            <div className="text-slate-300 text-sm mt-1">AI is editing your floor plan</div>
          </div>
        )}
      </div>

    </div>
  );
};

export default ImageCanvas;
